const DB_NAME = "swift_profile_cache"
const DB_VERSION = 1
const STORE = "photos"

let dbPromise = null

function openDb() {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)

    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" })
        store.createIndex("savedAt", "savedAt", { unique: false })
      }
    }

    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function txGet(store, key) {
  return new Promise((resolve, reject) => {
    const req = store.get(key)
    req.onsuccess = () => resolve(req.result || null)
    req.onerror = () => reject(req.error)
  })
}

function txPut(store, value) {
  return new Promise((resolve, reject) => {
    const req = store.put(value)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

function txDelete(store, key) {
  return new Promise((resolve, reject) => {
    const req = store.delete(key)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

export function normalizePhotoSrc(s) {
  if (!s || typeof s !== "string") return ""
  const trimmed = s.trim()
  if (trimmed.startsWith("data:image/")) return trimmed.replace(/\s/g, "")
  const compact = trimmed.replace(/\s/g, "")
  if (compact.startsWith("/9j/") || compact.startsWith("iVBORw0KGgo")) {
    const mime = compact.startsWith("iVBORw0KGgo") ? "image/png" : "image/jpeg"
    return `data:${mime};base64,${compact}`
  }
  return trimmed
}

export async function readCachedPhoto(id, ttlMs) {
  try {
    const key = String(id || "").trim()
    if (!key) return ""
    const db = await openDb()
    const tx = db.transaction([STORE], "readonly")
    const store = tx.objectStore(STORE)
    const rec = await txGet(store, key)

    if (!rec?.savedAt) return ""
    if (Date.now() - rec.savedAt > ttlMs) {
      const tx2 = db.transaction([STORE], "readwrite")
      const store2 = tx2.objectStore(STORE)
      await txDelete(store2, key)
      return ""
    }

    if (rec.notFound) return "__NOT_FOUND__"
    if (!rec.dataUrl) return ""
    return rec.dataUrl
  } catch {
    return ""
  }
}

export async function writeCachedPhoto(id, dataUrl) {
  try {
    const key = String(id || "").trim()
    if (!key || !dataUrl) return
    const db = await openDb()
    const tx = db.transaction([STORE], "readwrite")
    const store = tx.objectStore(STORE)
    await txPut(store, { id: key, dataUrl, savedAt: Date.now(), notFound: false })
  } catch {}
}

export async function writeCachedNotFound(id) {
  try {
    const key = String(id || "").trim()
    if (!key) return
    const db = await openDb()
    const tx = db.transaction([STORE], "readwrite")
    const store = tx.objectStore(STORE)
    await txPut(store, { id: key, dataUrl: "", savedAt: Date.now(), notFound: true })
  } catch {}
}

export async function purgeOldPhotoCache(ttlMs, maxEntries = 200) {
  try {
    const db = await openDb()
    const rows = await new Promise((resolve, reject) => {
      const tx = db.transaction([STORE], "readonly")
      const store = tx.objectStore(STORE)
      const req = store.getAll()
      req.onsuccess = () => resolve(req.result || [])
      req.onerror = () => reject(req.error)
    })

    const now = Date.now()
    const fresh = rows.filter(r => r?.id && r?.savedAt && (now - r.savedAt) <= ttlMs)
    const stale = rows.filter(r => !fresh.includes(r))

    if (stale.length) {
      const tx = db.transaction([STORE], "readwrite")
      const store = tx.objectStore(STORE)
      for (const r of stale) {
        try { await txDelete(store, r.id) } catch {}
      }
    }

    if (fresh.length <= maxEntries) return

    const sorted = [...fresh].sort((a, b) => (a.savedAt || 0) - (b.savedAt || 0))
    const extra = sorted.length - maxEntries
    const toRemove = sorted.slice(0, extra)

    const tx2 = db.transaction([STORE], "readwrite")
    const store2 = tx2.objectStore(STORE)
    for (const r of toRemove) {
      try { await txDelete(store2, r.id) } catch {}
    }
  } catch {}
}
